import React, { useRef, useState } from 'react';
import Button from '@amzn/meridian/button';
import undoTokens from '@amzn/meridian-tokens/base/icon/undo';
import Row from '@amzn/meridian/row';
import Column from '@amzn/meridian/column';
import Table, { TableCell, TableRow } from '@amzn/meridian/table';
import Text from '@amzn/meridian/text';
import Link from '@amzn/meridian/link';
import Popover, { PopoverHeader } from '@amzn/meridian/popover';
import {
  SSD_OVERRIDE_REASON_CODES,
  SSD_FEATURE_FLAGS_LIST,
  BUSINESS_VIEW_NAME,
  CORE_BUSINESS_TYPE_LABEL
} from '../../../../utils/constants';
import Select, { SelectOption } from '@amzn/meridian/select';
import Input from '@amzn/meridian/input';
import { chainWalk } from '../../../../utils/helpers';
import Icon from '@amzn/meridian/icon';
import { getShiftSchedules, getTimelineForViewPlan, isFeatureFlagEnabled } from '../../utils';
import NavigationButtons from '../../../commonComponents/NavigationButtons';

const USER_OVERRIDE_REASON_CODES = [
  SSD_OVERRIDE_REASON_CODES.EXCESSIVE_ATTRITION,
  SSD_OVERRIDE_REASON_CODES.PLANNED_EVENT
];

const VerifyHeadcounts = ({ currentUser, plan, setPlan, onGoBack, onGoNext, onCancel }) => {
  const isGroceryEnabled = isFeatureFlagEnabled(plan.input.adminSettings, SSD_FEATURE_FLAGS_LIST.groceryEnabled);
  const viewComponent = isGroceryEnabled ? (
    <HeadcountsComponentByBusiness currentUser={currentUser} plan={plan} setPlan={setPlan} />
  ) : (
    <HeadcountsComponent currentUser={currentUser} plan={plan} setPlan={setPlan} />
  );

  return (
    <Column width="100%">
      <Text type={'h400'}>Headcount</Text>
      <Text type={'b400'}>Verify or update the values below and submit.</Text>

      {viewComponent}

      <NavigationButtons onBack={onGoBack} onNext={onGoNext} onCancel={onCancel} showCancel={true} />
    </Column>
  );
};

const OverrideHeadcountPopover = ({ currentUser, plan, setPlan, headcount }) => {
  const linkRef = useRef();
  const [open, setOpen] = useState(false);
  const { scheduledHeadcount, onsiteHeadcount, availableHeadcount, expectedAttendanceHeadcount } = headcount || {};

  const overridingValue = chainWalk(() => headcount.overrides.overrideKeyValueMap.availableHeadcount, null);
  const reasonCode = chainWalk(() => headcount.overrides.reason, null);
  const minHeadcount = chainWalk(() => headcount.overrides.overrideKeyGuardrailsMap.availableHeadcount.minimum, 0);
  const maxHeadcount = chainWalk(
    () => headcount.overrides.overrideKeyGuardrailsMap.availableHeadcount.maximum,
    Number.POSITIVE_INFINITY
  );

  const initialValue = overridingValue != null ? overridingValue : availableHeadcount;
  const [newOverridingValue, setNewOverridingValue] = useState(initialValue);
  const [selectedOverrideReason, setSelectedOverrideReason] = useState(reasonCode);
  const attendancePercentage =
    scheduledHeadcount != null ? Math.round((newOverridingValue / scheduledHeadcount) * 100) : '';

  const onSubmit = () => {
    if (!headcount.overrides) {
      headcount.overrides = {};
    }
    if (!headcount.overrides.overrideKeyValueMap) {
      headcount.overrides.overrideKeyValueMap = {};
    }
    headcount.overrides.overrideKeyValueMap.availableHeadcount = newOverridingValue + '';
    headcount.overrides.reason = selectedOverrideReason;
    headcount.overrides.timestamp = parseInt(Date.now() / 1000);
    headcount.overrides.user = currentUser;
    setPlan({ ...plan });
    setOpen(false);
  };
  const onRevert = () => {
    if (headcount.overrides) {
      delete headcount.overrides.overrideKeyValueMap;
      delete headcount.overrides.users;
      delete headcount.overrides.reason;
    }
    setNewOverridingValue(availableHeadcount);
    setSelectedOverrideReason(null);
    setPlan({ ...plan });
  };

  const isValid = minHeadcount <= newOverridingValue && newOverridingValue <= maxHeadcount;
  const isDisabled = initialValue === newOverridingValue || !isValid || !selectedOverrideReason;
  const isRevertible = overridingValue !== null;

  return (
    <React.Fragment>
      {isRevertible && (
        <Button type={'link'} onClick={onRevert}>
          <Icon tokens={undoTokens} />
        </Button>
      )}
      <Link type="secondary" onClick={() => setOpen(true)} ref={linkRef}>
        {overridingValue != null ? overridingValue : availableHeadcount}
      </Link>
      <Popover anchorNode={linkRef.current} open={open} onClose={() => setOpen(false)} position="bottom">
        <PopoverHeader closeLabel="Close">Provide an override</PopoverHeader>
        <Column>
          <Row width="100%" alignmentHorizontal="justify">
            <Text>Scheduled HC</Text>
            <Text>{scheduledHeadcount}</Text>
          </Row>
          <Row width="100%" alignmentHorizontal="justify">
            <Text>Onsite HC</Text>
            <Text>{onsiteHeadcount}</Text>
          </Row>
          <Row width="100%" alignmentHorizontal="justify">
            <Text>Expected Attendance HC</Text>
            <Text alignment="end">{expectedAttendanceHeadcount}</Text>
          </Row>
          <Row width="100%" alignmentHorizontal="justify">
            <Text>Available HC</Text>
            <Input
              width={200}
              value={newOverridingValue}
              onChange={setNewOverridingValue}
              error={!isValid}
              type="number"
              size="small"
              errorMessage={!isValid ? `Headcount value must be between ${minHeadcount} and ${maxHeadcount}` : ''}
            />
          </Row>
          <Row width="100%" widths="fill">
            <Text>Shift Half Attendance Rate</Text>
            <Text alignment="end">{attendancePercentage}%</Text>
          </Row>
          <Select
            value={selectedOverrideReason}
            onChange={setSelectedOverrideReason}
            placeholder="Select a reason"
            size="small">
            {USER_OVERRIDE_REASON_CODES.map(({ value, label }) => (
              <SelectOption key={value} value={value} label={label} />
            ))}
          </Select>
          <Button disabled={isDisabled} onClick={onSubmit}>
            Submit Override
          </Button>
        </Column>
      </Popover>
    </React.Fragment>
  );
};

const HeadcountsComponent = ({ currentUser, plan, setPlan }) => {
  const timeline = getTimelineForViewPlan(plan);
  const headcounts = chainWalk(() => plan.input.ssdShiftPlanInput.headcountList, []);
  const shiftSchedules = getShiftSchedules(timeline);
  const headcountRows = [];
  shiftSchedules.forEach((shiftSchedule) => {
    const firstHalfHeadcount = chainWalk(
      () =>
        headcounts.find((headcount) => headcount.shiftHalfStartTimestamp === shiftSchedule.firstHalf.startTimestamp),
      {}
    );
    const secondHalfHeadcount = chainWalk(
      () =>
        headcounts.find((headcount) => headcount.shiftHalfStartTimestamp === shiftSchedule.secondHalf.startTimestamp),
      {}
    );
    headcountRows.push(
      <TableRow key={shiftSchedule.label}>
        <TableCell>
          <Text type="h100">{shiftSchedule.label}</Text>
        </TableCell>
        <TableCell>{firstHalfHeadcount.scheduledHeadcount}</TableCell>
        <TableCell>
          <OverrideHeadcountPopover
            currentUser={currentUser}
            plan={plan}
            setPlan={setPlan}
            headcount={firstHalfHeadcount}
          />
        </TableCell>
        <TableCell>{secondHalfHeadcount.scheduledHeadcount}</TableCell>
        <TableCell>
          <OverrideHeadcountPopover
            currentUser={currentUser}
            plan={plan}
            setPlan={setPlan}
            headcount={secondHalfHeadcount}
          />
        </TableCell>
      </TableRow>
    );
  });

  return (
    <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
      <Table headerRows={1} showDividers={true}>
        <TableRow>
          <TableCell>Window</TableCell>
          <TableCell>1H Scheduled</TableCell>
          <TableCell>1H Available</TableCell>
          <TableCell>2H Scheduled</TableCell>
          <TableCell>2H Available</TableCell>
        </TableRow>
        <TableRow backgroundColor="secondary">
          <TableCell>Active Shift</TableCell>
          <TableCell columnSpan={4}></TableCell>
        </TableRow>
        {headcountRows[0]}
        <TableRow backgroundColor="secondary">
          <TableCell>Upcoming Shifts</TableCell>
          <TableCell columnSpan={4}></TableCell>
        </TableRow>
        {headcountRows.slice(1)}
      </Table>
    </div>
  );
};

const HeadcountsComponentByBusiness = ({ currentUser, plan, setPlan }) => {
  const timeline = getTimelineForViewPlan(plan);
  const headcounts = chainWalk(() => plan.input.ssdShiftPlanInput.headcountList, []);
  const shiftSchedules = getShiftSchedules(timeline);

  const getAvailableHeadcount = (headcount) => {
    if (!headcount) return 0;
    const existingOverrides = chainWalk(() => headcount.overrides.overrideKeyValueMap.availableHeadcount, null);
    const availableHeadcount = headcount.availableHeadcount ?? 0;
    const result = existingOverrides != null ? Number(existingOverrides) : Number(availableHeadcount);
    return Number.isFinite(result) ? result : 0;
  };

  const findHeadcountByBusiness = (startTimestamp, business) => {
    if (startTimestamp == null) return null;
    return (
      headcounts.find((headcount) => {
        if (headcount.shiftHalfStartTimestamp !== startTimestamp) return false;
        return headcount.businessType === business;
      }) || null
    );
  };

  const overrideCell = (headcount) =>
    headcount ? (
      <OverrideHeadcountPopover currentUser={currentUser} plan={plan} setPlan={setPlan} headcount={headcount} />
    ) : (
      <>-</>
    );

  const buildWindowRows = (label, firstHalfStart, secondHalfStart) => {
    const coreFirstHalf = findHeadcountByBusiness(firstHalfStart, BUSINESS_VIEW_NAME.CORE);
    const coreSecondHalf = findHeadcountByBusiness(secondHalfStart, BUSINESS_VIEW_NAME.CORE);
    const groceryFirstHalf = findHeadcountByBusiness(firstHalfStart, BUSINESS_VIEW_NAME.GROCERY);
    const grocerySecondHalf = findHeadcountByBusiness(secondHalfStart, BUSINESS_VIEW_NAME.GROCERY);

    const core = {
      scheduled1H: Number(coreFirstHalf?.scheduledHeadcount ?? 0),
      available1H: getAvailableHeadcount(coreFirstHalf),
      scheduled2H: Number(coreSecondHalf?.scheduledHeadcount ?? 0),
      available2H: getAvailableHeadcount(coreSecondHalf)
    };
    const grocery = {
      scheduled1H: Number(groceryFirstHalf?.scheduledHeadcount ?? 0),
      available1H: getAvailableHeadcount(groceryFirstHalf),
      scheduled2H: Number(grocerySecondHalf?.scheduledHeadcount ?? 0),
      available2H: getAvailableHeadcount(grocerySecondHalf)
    };
    const total = {
      scheduled1H: core.scheduled1H + grocery.scheduled1H,
      available1H: core.available1H + grocery.available1H,
      scheduled2H: core.scheduled2H + grocery.scheduled2H,
      available2H: core.available2H + grocery.available2H
    };

    const rows = [];

    rows.push(
      <TableRow key={`${label}-${BUSINESS_VIEW_NAME.CORE}`}>
        <TableCell>{label}</TableCell>
        <TableCell>{CORE_BUSINESS_TYPE_LABEL}</TableCell>
        <TableCell>{core.scheduled1H}</TableCell>
        <TableCell>{overrideCell(coreFirstHalf)}</TableCell>
        <TableCell>{core.scheduled2H}</TableCell>
        <TableCell>{overrideCell(coreSecondHalf)}</TableCell>
      </TableRow>
    );

    rows.push(
      <TableRow key={`${label}-${BUSINESS_VIEW_NAME.GROCERY}`}>
        <TableCell />
        <TableCell>Grocery</TableCell>
        <TableCell>{grocery.scheduled1H}</TableCell>
        <TableCell>{overrideCell(groceryFirstHalf)}</TableCell>
        <TableCell>{grocery.scheduled2H}</TableCell>
        <TableCell>{overrideCell(grocerySecondHalf)}</TableCell>
      </TableRow>
    );

    rows.push(
      <TableRow key={`${label}-total`} backgroundColor="secondary">
        <TableCell>Window Total</TableCell>
        <TableCell />
        <TableCell>{total.scheduled1H}</TableCell>
        <TableCell>{total.available1H}</TableCell>
        <TableCell>{total.scheduled2H}</TableCell>
        <TableCell>{total.available2H}</TableCell>
      </TableRow>
    );

    return { rows, total };
  };

  const windowBlocks = shiftSchedules.map((shiftSchedule) =>
    buildWindowRows(
      shiftSchedule.label,
      chainWalk(() => shiftSchedule.firstHalf.startTimestamp, null),
      chainWalk(() => shiftSchedule.secondHalf.startTimestamp, null)
    )
  );

  const first = windowBlocks[0];
  const rest = windowBlocks.slice(1);
  const rows = [];

  rows.push(
    <TableRow key="headerRow">
      <TableCell>Window</TableCell>
      <TableCell>Business</TableCell>
      <TableCell>1H Scheduled</TableCell>
      <TableCell>1H Available</TableCell>
      <TableCell>2H Scheduled</TableCell>
      <TableCell>2H Available</TableCell>
    </TableRow>
  );

  if (first) {
    rows.push(
      <TableRow key="active-header" backgroundColor="secondary">
        <TableCell>Active Shift</TableCell>
        <TableCell columnSpan={5}></TableCell>
      </TableRow>
    );
    rows.push(...first.rows);
  }

  if (rest.length > 0) {
    rows.push(
      <TableRow key="upcoming-header" backgroundColor="secondary">
        <TableCell>Upcoming Shifts</TableCell>
        <TableCell columnSpan={5}></TableCell>
      </TableRow>
    );
    rest.forEach((b, i) => rows.push(...b.rows));
  }

  return (
    <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
      <Table headerRows={1} showDividers={true}>
        {rows}
      </Table>
    </div>
  );
};

export default VerifyHeadcounts;
